import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  SetNextInvoiceNumber,
  UpdateOrganizationAccess,
  UpdateOrganizationBusiness,
  UpdateOrganizationInvoicing,
  UpdateOrganizationPaymentOptions,
  UpdateOrganizationTax,
  UpdateOrganizationTerms,
} from '@trellis/shared'

import {
  acceptOrganizationTerms,
  claimOnboardingCelebration,
  deleteOrganizationLogo,
  deleteOrganizationLogoSmall,
  setNextInvoiceNumber,
  updateOrganizationAccess,
  updateOrganizationBusiness,
  updateOrganizationInvoicing,
  updateOrganizationPaymentOptions,
  updateOrganizationTax,
  uploadOrganizationLogo,
  uploadOrganizationLogoSmall,
} from '@/requests/organization'

function useInvalidateOrganization() {
  const queryClient = useQueryClient()
  return async () => await queryClient.invalidateQueries({ queryKey: ['organization'] })
}

export function useUpdateOrganizationAccessMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (data: UpdateOrganizationAccess) => updateOrganizationAccess(data),
    onSuccess: invalidate,
  })
}

export function useUpdateOrganizationBusinessMutation() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (data: UpdateOrganizationBusiness) => updateOrganizationBusiness(data),
    onSuccess: async () => {
      await Promise.all([
        invalidate(),
        queryClient.invalidateQueries({ queryKey: ['tenant-memberships'] }),
      ])
    },
  })
}

export function useClaimOnboardingCelebrationMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: claimOnboardingCelebration,
    onSuccess: invalidate,
  })
}

export function useAcceptOrganizationTermsMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (data: UpdateOrganizationTerms) => acceptOrganizationTerms(data),
    onSuccess: invalidate,
  })
}

export function useUpdateOrganizationTaxMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (data: UpdateOrganizationTax) => updateOrganizationTax(data),
    onSuccess: invalidate,
  })
}

export function useUpdateOrganizationInvoicingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateOrganizationInvoicing) => updateOrganizationInvoicing(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organization'] })
      // A changed prefix changes what the next system-generated number looks like.
      await queryClient.invalidateQueries({ queryKey: ['invoices', 'next-number'] })
    },
  })
}

export function useSetNextInvoiceNumberMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SetNextInvoiceNumber) => setNextInvoiceNumber(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organization'] })
      await queryClient.invalidateQueries({ queryKey: ['invoices', 'next-number'] })
    },
  })
}

export function useUpdateOrganizationPaymentOptionsMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (data: UpdateOrganizationPaymentOptions) =>
      updateOrganizationPaymentOptions(data),
    onSuccess: invalidate,
  })
}

export function useUploadOrganizationLogoMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (file: File) => uploadOrganizationLogo(file),
    onSuccess: invalidate,
  })
}

export function useDeleteOrganizationLogoMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: () => deleteOrganizationLogo(),
    onSuccess: invalidate,
  })
}

export function useUploadOrganizationLogoSmallMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: (file: File) => uploadOrganizationLogoSmall(file),
    onSuccess: invalidate,
  })
}

export function useDeleteOrganizationLogoSmallMutation() {
  const invalidate = useInvalidateOrganization()
  return useMutation({
    mutationFn: () => deleteOrganizationLogoSmall(),
    onSuccess: invalidate,
  })
}
